<template>
  <div class="settings-container">
    <div class="page-header">
      <h2>温馨提示管理</h2>
      <el-button type="primary" @click="showAddDialog">新增提示</el-button>
    </div>

    <!-- 搜索区域 -->
    <div class="search-section">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="提示类型">
          <el-select v-model="searchForm.type" placeholder="请选择提示类型" clearable>
            <el-option label="信息" value="info"></el-option>
            <el-option label="警告" value="warning"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="标题关键词">
          <el-input v-model="searchForm.title" placeholder="请输入标题关键词" clearable></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 提示列表 -->
    <div class="table-section">
      <el-table :data="tipList" v-loading="loading" border stripe>
        <el-table-column prop="tipId" label="提示ID" width="100"></el-table-column>
        <el-table-column prop="title" label="标题" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="content" label="内容" width="300" show-overflow-tooltip></el-table-column>
        <el-table-column prop="type" label="类型" width="80">
          <template slot-scope="scope">
            <el-tag :type="getTypeTagType(scope.row.type)">
              {{ getTypeLabel(scope.row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sortOrder" label="排序" width="60"></el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="showEditDialog(scope.row)">编辑</el-button>
            <el-button size="mini" type="success" @click="showDetailDialog(scope.row)">详情</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.current"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>

    <!-- 新增/编辑提示对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" @close="resetForm">
      <el-form :model="tipForm" :rules="tipRules" ref="tipForm" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="提示ID" prop="tipId">
              <el-input v-model="tipForm.tipId" placeholder="请输入提示ID" :disabled="isEdit"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="提示类型" prop="type">
              <el-select v-model="tipForm.type" placeholder="请选择提示类型" style="width: 100%">
                <el-option label="信息" value="info"></el-option>
                <el-option label="警告" value="warning"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="排序" prop="sortOrder">
              <el-input-number v-model="tipForm.sortOrder" :min="0" placeholder="排序" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="标题" prop="title">
          <el-input v-model="tipForm.title" placeholder="请输入标题"></el-input>
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input type="textarea" v-model="tipForm.content" :rows="6" placeholder="请输入内容"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 提示详情对话框 -->
    <el-dialog title="温馨提示详情" :visible.sync="detailDialogVisible" width="600px">
      <div v-if="currentTip" class="tip-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="提示ID">{{ currentTip.tipId }}</el-descriptions-item>
          <el-descriptions-item label="提示类型">
            <el-tag :type="getTypeTagType(currentTip.type)">
              {{ getTypeLabel(currentTip.type) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="标题" :span="2">{{ currentTip.title }}</el-descriptions-item>
          <el-descriptions-item label="内容" :span="2">{{ currentTip.content }}</el-descriptions-item>
          <el-descriptions-item label="排序">{{ currentTip.sortOrder }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { tipApi } from '@/api/tip'

export default {
  name: 'Settings',
  data() {
    return {
      loading: false,
      submitLoading: false,
      tipList: [],
      searchForm: {
        type: '',
        title: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      detailDialogVisible: false,
      isEdit: false,
      currentTip: null,
      tipForm: {
        tipId: '',
        title: '',
        content: '',
        type: 'info',
        sortOrder: 0
      },
      tipRules: {
        tipId: [
          { required: true, message: '请输入提示ID', trigger: 'blur' }
        ],
        title: [
          { required: true, message: '请输入标题', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入内容', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择提示类型', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑温馨提示' : '新增温馨提示'
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    // 加载数据
    async loadData() {
      this.loading = true
      try {
        const params = {
          current: this.pagination.current,
          size: this.pagination.size,
          ...this.searchForm
        }
        const response = await tipApi.getTipList(params)
        if (response && (response.code === 200 || response.code === '200') && response.data) {
          this.tipList = response.data.records || []
          this.pagination.total = response.data.total || 0
        }
      } catch (error) {
        console.error('加载温馨提示数据失败:', error)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        type: '',
        title: ''
      }
      this.handleSearch()
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size
      this.loadData()
    },

    // 当前页改变
    handleCurrentChange(current) {
      this.pagination.current = current
      this.loadData()
    },

    // 显示新增对话框
    showAddDialog() {
      this.isEdit = false
      this.dialogVisible = true
    },

    // 显示编辑对话框
    showEditDialog(row) {
      this.isEdit = true
      this.currentTip = row
      this.tipForm = { ...row }
      this.dialogVisible = true
    },

    // 显示详情对话框
    showDetailDialog(row) {
      this.currentTip = row
      this.detailDialogVisible = true
    },

    // 提交表单
    async handleSubmit() {
      this.$refs.tipForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            if (this.isEdit) {
              await tipApi.updateTip(this.tipForm)
              this.$message.success('更新成功')
            } else {
              await tipApi.addTip(this.tipForm)
              this.$message.success('添加成功')
            }
            this.dialogVisible = false
            this.loadData()
          } catch (error) {
            console.error('提交失败:', error)
            this.$message.error('操作失败')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 删除提示
    handleDelete(row) {
      this.$confirm('确定要删除该温馨提示吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await tipApi.deleteTip(row.tipId)
          this.$message.success('删除成功')
          this.loadData()
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },

    // 重置表单
    resetForm() {
      this.$refs.tipForm.resetFields()
      this.tipForm = {
        tipId: '',
        title: '',
        content: '',
        type: 'info',
        sortOrder: 0
      }
    },

    // 获取提示类型标签类型
    getTypeTagType(type) {
      const types = {
        'info': 'primary',
        'warning': 'warning'
      }
      return types[type] || 'info'
    },

    // 获取提示类型标签
    getTypeLabel(type) {
      const labels = {
        'info': '信息',
        'warning': '警告'
      }
      return labels[type] || type
    }
  }
}
</script>

<style scoped>
.settings-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.search-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-form {
  margin: 0;
}

.table-section {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pagination-section {
  padding: 20px;
  text-align: right;
}

/* 操作按钮样式优化 */
.el-button--mini {
  margin-right: 5px;
}

.el-button--mini:last-child {
  margin-right: 0;
}

/* 表格内容垂直居中 */
.el-table .cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.el-table .cell:first-child {
  justify-content: flex-start;
}

/* 操作列按钮容器 */
.el-table .el-button + .el-button {
  margin-left: 5px;
}

.tip-detail {
  padding: 20px 0;
}

.dialog-footer {
  text-align: right;
}
</style>